import { createStore } from 'zustand';
import type { StoreApi } from 'zustand';

import {
  EntityPageContractType,
  EntityPageFocusType,
  EntityPagePublicationStatus,
} from '@xing-com/crate-common-graphql-types';
import type { NavigateFn } from '@xing-com/crate-core-router';
import { PAGES_PATH } from '@xing-com/crate-entity-pages-entity-pages-shared-urls';
import { BREAKPOINTS } from '@xing-com/util';

import type { EntityPageData } from '../../types/entity-page';
import type { EntityPageStore } from '../../types/entity-page-store';
import type { HeaderModuleData } from '../../types/header-module';

type EntityPageProps = {
  entityPage: EntityPageData;
  headerModule?: HeaderModuleData;
  navigate: NavigateFn;
  isWebview: boolean;
  isSubpage?: boolean;
};
export const createEntityPageStore = ({
  entityPage,
  headerModule,
  navigate,
  isWebview,
  isSubpage,
}: EntityPageProps): StoreApi<EntityPageStore> => {
  const contractType = entityPage.contract?.type;
  const focusType = entityPage.focusType!;
  const publicationStatus = entityPage.publicationStatus;
  const isEditor = !!entityPage.userPageContext?.permissions?.canEdit;
  const isAdmin = !!entityPage.userPageContext?.permissions?.canAdmin;

  return createStore<EntityPageStore>()((set) => ({
    pageContext: {
      globalId: entityPage.globalId,
      pageSlug: entityPage.slug,
      pageId: entityPage.id,
      pageSlogan: entityPage.slogan ?? '',
      visibility: entityPage.visibility,
      url: entityPage.links?.self,
      pageTitle: entityPage.title,
      isFollowingPage:
        entityPage.userPageContext?.userInteractions?.__typename ===
          'EntityPageUserInteractionFollow' &&
        entityPage.userPageContext.userInteractions.followState?.isFollowing,
      logo: entityPage.logoImage?.[0].url ?? '',
      coverImage: entityPage.coverImage ?? undefined,
      isEditor,
      isAdmin,
      // Focus Type
      focusType: focusType,
      isContentPage: [
        EntityPageFocusType.GroupPage,
        EntityPageFocusType.PremiumPage,
        EntityPageFocusType.IndustryPage,
        EntityPageFocusType.Publisher,
        EntityPageFocusType.TopicPage,
        EntityPageFocusType.Insider,
      ].includes(focusType),
      isCompanyPage: focusType === EntityPageFocusType.Company,
      isGroupPage: focusType === EntityPageFocusType.GroupPage,
      isPremiumPage: focusType === EntityPageFocusType.PremiumPage,
      isIndustryPage: focusType === EntityPageFocusType.IndustryPage,
      isPublisherPage: focusType === EntityPageFocusType.Publisher,
      isTopicPage: focusType === EntityPageFocusType.TopicPage,
      isInsiderPage: focusType === EntityPageFocusType.Insider,
      // Contract Type
      contractType,
      isFreePage: contractType === EntityPageContractType.Free,
      isPaidPage:
        contractType === EntityPageContractType.PaidPlus ||
        contractType === EntityPageContractType.Paid,
      isEBP: contractType === EntityPageContractType.Paid,
      // Publication Status
      publicationStatus,
      isPageDraft: publicationStatus === EntityPagePublicationStatus.Draft,
      isPagePublished:
        publicationStatus === EntityPagePublicationStatus.Published,
      // Company Page Values
      companyId: entityPage.context?.companyId,
      cppId: entityPage.context?.cppId,
      socialProof: entityPage.userPageContext?.socialProof,
      basePath: `/pages${isWebview ? '/webview' : ''}`,
      upsellActive:
        entityPage.contract?.type === 'FREE' &&
        (isAdmin || isEditor) &&
        entityPage.focusType === EntityPageFocusType.Company,
      isShowingCompanyBanner: false,
      // Content Pages
      contentTopicCollection:
        entityPage.entity?.__typename === 'ContentInsiderPage'
          ? entityPage.entity?.topics?.collection
          : undefined,
      insiderPageName:
        entityPage.entity?.__typename === 'ContentInsiderPage'
          ? entityPage.entity?.xingId?.pageName
          : undefined,
      contentPageGlobalId: entityPage.entity?.globalId,
      // Header Module
      headerModuleCta: headerModule?.properties?.cta,
      followersTotal:
        headerModule?.content && 'followers' in headerModule.content
          ? (headerModule?.content.followers?.total ?? 0)
          : 0,
      employeesCount:
        headerModule?.content && 'company' in headerModule.content
          ? (headerModule?.content.company?.employeesCount ?? undefined)
          : undefined,
      companySizeRange:
        headerModule?.content && 'company' in headerModule.content
          ? {
              max: headerModule?.content.company?.companySizeRange?.max,
              min: headerModule?.content.company?.companySizeRange?.min,
            }
          : undefined,
      companyIndustryId:
        headerModule?.content && 'company' in headerModule.content
          ? headerModule?.content.company?.industry?.id
          : undefined,
      // Utils
      goBackUrl: entityPage.globalId
        ? () => navigate(-1)
        : () => navigate(PAGES_PATH(entityPage.slug)),
    },
    isSubpage,
    showCompanyBanner: () =>
      set((state) => ({
        pageContext: {
          ...state.pageContext,
          isShowingCompanyBanner: true,
        },
      })),
    toggleFollowPage: (follow: boolean): void => {
      set((state) => ({
        pageContext: {
          ...state.pageContext,
          isFollowingPage: follow,
          followersTotal: state.pageContext.followersTotal + (follow ? 1 : -1),
        },
      }));
    },
    setPageLogo: (logo: string): void => {
      set((state) => ({
        pageContext: {
          ...state.pageContext,
          logo,
        },
      }));
    },
    setCoverImage: (coverUrl?: string): void => {
      set((state) => ({
        pageContext: {
          ...state.pageContext,
          coverImage: coverUrl
            ? Object.values(BREAKPOINTS).map((reference) => ({
                __typename: 'ScaledImage' as const,
                url: coverUrl,
                reference: reference,
              }))
            : undefined,
        },
      }));
    },
  }));
};
